/**
 * The status projection: the JSON the backend writes onto a content's AI internal data property
 * when a media generation changes state — `{"status":"suggested"|"generating"|"completed"|"failed",
 * "statusChangedUtc":"…"}`. "suggested" is the one status no generation writes: ingestion leaves it
 * to say the page AI planned a picture for this slot that nobody has drawn yet.
 *
 * Read for rendering only. The backend's generation table is the source of truth for whether a
 * generation is in flight; a projection can be lost or stale, so nothing may treat it as more than
 * what a page load happened to see. Parsing therefore never throws: any payload that does not spell
 * a projection — the property's other uses, junk, half a write — reads as no status at all.
 */

export type TMediaGenerationStatus = 'suggested' | 'generating' | 'completed' | 'failed';

export interface IMediaGenerationStatusProjection {
  status: TMediaGenerationStatus;
  /** When the status came to stand this way, epoch ms — or null when the payload carried no readable time. */
  statusChangedUtcMs: number | null;
}

const KNOWN_STATUSES: readonly string[] = ['suggested', 'generating', 'completed', 'failed'];

/** Whether the timestamp names its zone. The field is UTC by contract, so one that does not still reads as UTC. */
const hasUtcDesignator = (timestamp: string): boolean => /(Z|z|[+-]\d\d:?\d\d)$/.test(timestamp);

const parseStatusChangedUtc = (value: unknown): number | null => {
  if (typeof value !== 'string' || value.length === 0) return null;

  const parsed = Date.parse(hasUtcDesignator(value) ? value : `${value}Z`);
  return Number.isNaN(parsed) ? null : parsed;
};

export const parseMediaGenerationStatusProjection = (rawAiInternalData: string | null | undefined): IMediaGenerationStatusProjection | null => {
  if (!rawAiInternalData) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(rawAiInternalData);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null) return null;

  const { status, statusChangedUtc } = payload as { status?: unknown; statusChangedUtc?: unknown };
  if (typeof status !== 'string' || !KNOWN_STATUSES.includes(status)) return null;

  return { status: status as TMediaGenerationStatus, statusChangedUtcMs: parseStatusChangedUtc(statusChangedUtc) };
};
